/**
 * FauxDS BrandPortalModal - Updated to match real Slate Modal component
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Modal for prototyping.
 * It is NOT the real @faire/slate Modal and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Modal, see: @faire/slate/components/modal
 *
 * Updated to match the real Slate Modal structure:
 * - White footer (no gray background), conditional top border
 * - Desktop padding: 32px horizontal (--slate-dimensions-8)
 * - Header: 32px top padding, subheading-medium title
 * - Close button: IconButton style, absolute positioned
 * - 80vh max height on desktop, 90dvh on mobile
 * - 400px default width, 648px large
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { useEffect, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface BrandPortalModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Optional subline under the title for step context */
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  contextualInfo?: React.ReactNode;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  /** Modal width — "default" (400px) or "large" (648px) or custom px string */
  width?: string;
  /** Optional class names for the footer region */
  footerClassName?: string;
}

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 4L12 12M12 4L4 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function BrandPortalModal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  contextualInfo,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = "",
  width = "520px",
  footerClassName = "",
}: BrandPortalModalProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === "Escape") {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handleKeyDown]);

  // Track scroll position to show/hide footer border
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;

    const checkScroll = () => {
      const atBottom =
        body.scrollHeight - body.scrollTop - body.clientHeight < 2;
      setIsAtBottom(atBottom);
    };

    checkScroll();
    body.addEventListener("scroll", checkScroll);
    // Re-check after content may have changed
    const observer = new ResizeObserver(checkScroll);
    observer.observe(body);

    return () => {
      body.removeEventListener("scroll", checkScroll);
      observer.disconnect();
    };
  }, [open, children]);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-1000 grid place-items-center"
      style={{ backgroundColor: "rgba(51, 51, 51, 0.5)" }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      {/* Modal container */}
      <div
        className={cn(
          "bg-fs-surface-primary relative mx-4 flex max-h-[90dvh] animate-[modal-slide-in_250ms_ease-out] flex-col overflow-hidden md:max-h-[80dvh]",
          className
        )}
        style={{
          width,
          maxWidth: "calc(100vw - 32px)",
          borderRadius: "var(--slate-radius-component-default, 4px)",
          boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button — absolute positioned top-right */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-[var(--slate-dimensions-5,20px)] right-[var(--slate-dimensions-5,20px)] z-10 flex h-[var(--slate-dimensions-6,24px)] w-[var(--slate-dimensions-6,24px)] items-center justify-center rounded-[var(--slate-radius-component-default,4px)] text-[var(--slate-color-text-primary,#333)] transition-colors duration-150 hover:bg-[var(--slate-color-action-surface-inverse-hover,#f7f7f7)] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring,#275ec5)] focus-visible:ring-offset-2"
          aria-label="Close modal"
        >
          <CloseIcon />
        </button>

        {/* Header */}
        <div className="shrink-0 pt-[var(--slate-dimensions-8,32px)] pr-[calc(var(--slate-dimensions-8,32px)+var(--slate-dimensions-6,24px)+var(--slate-dimensions-4,16px))] pb-[var(--slate-dimensions-5,20px)] pl-[var(--slate-dimensions-8,32px)]">
          <h2
            id="modal-title"
            style={{
              fontFamily:
                "var(--slate-typography-family-sans, 'Graphik', sans-serif)",
              fontSize: "18px",
              fontWeight: 500,
              lineHeight: "24px",
              letterSpacing: "0.15px",
              color: "var(--slate-color-text-primary, #333333)",
            }}
          >
            {title}
          </h2>
          {description ? (
            <p className="type-fs-paragraph text-fs-text-subdued mt-(--slate-spacing-element-xsmall)">
              {description}
            </p>
          ) : null}
        </div>

        {/* Body — scrollable */}
        <div
          ref={bodyRef}
          className="min-h-0 flex-1 overflow-y-auto px-[var(--slate-dimensions-8,32px)]"
          style={{
            fontFamily:
              "var(--slate-typography-family-sans, 'Graphik', sans-serif)",
            fontSize: "14px",
            lineHeight: "20px",
            color: "var(--slate-color-text-primary, #333333)",
          }}
        >
          {children}
        </div>

        {/* Footer — #fbfbfb background, conditional top border */}
        {(footer || contextualInfo) && (
          <div
            className={cn(
              "shrink-0 border-t bg-[#fbfbfb] px-[var(--slate-dimensions-8,32px)] py-[var(--slate-dimensions-6,24px)] transition-colors duration-150",
              footerClassName
            )}
            style={{
              borderColor: isAtBottom
                ? "transparent"
                : "var(--slate-color-border-muted, #DFE0E1)",
            }}
          >
            {contextualInfo && (
              <div
                className="mb-[var(--slate-dimensions-3,12px)]"
                style={{
                  fontFamily:
                    "var(--slate-typography-family-sans, 'Graphik', sans-serif)",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "16px",
                  color: "var(--slate-color-text-primary, #333333)",
                }}
              >
                {contextualInfo}
              </div>
            )}
            {footer}
          </div>
        )}
      </div>

      <style>{`
        @keyframes modal-slide-in {
          from {
            opacity: 0;
            transform: translateY(10%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
}
